import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {LayoutRecalc, LayoutUtils} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LayoutUtils';
import {PositioningType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/PositioningType';
import {Page, PageId} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {
    SelectionInteractionMode,
    SurfaceAreaState
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {CompassDir} from '@mintdata/mint-kit/api/models/CompassDir';
import {BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {action} from 'mobx';
import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';

type SurfaceSelectionListener = {
    onSurfaceSelectionChanged:() => void;
}

export class SurfaceSelectionController implements HasEvents<SurfaceSelectionListener>
{
    public getSelectedBoxes():Box[]
    {
        const state = this._getState();
        return state.selectionState.selectedBoxesOnCurrentLayerPage;
    }

    public getOrderedSelectedBoxes():Box[]
    {
        const {surfaceArea} = controllers.ui;
        const surfaceAreaState = surfaceArea.getState();

        if( surfaceAreaState )
        {
            return this.getSelectedBoxes().sort((a, b) => (
                surfaceArea.getBoxIndex(a) - surfaceArea.getBoxIndex(b)
            ));
        }

        return [];
    }

    public isBoxSelected(box:Box):boolean
    {
        const state = this._getState();
        const idx = this._findSelectedBoxIndex(state, box);

        return idx !== -1;
    }

    public isOnlyViewportSelected():boolean
    {
        const selectedBoxes:Box[] = this.getSelectedBoxes();

        return selectedBoxes.length === 1 && selectedBoxes[0].isViewport;
    }

    public hasViewportInSelected():boolean
    {
        const selectedBoxes:Box[] = this.getSelectedBoxes();

        for( let i = 0; i < selectedBoxes.length; i++ )
        {
            if( selectedBoxes[i].isViewport )
                return true;
        }

        return false;
    }

    public areMultipleBoxesSelected():boolean
    {
        const state = this._getState();

        return state.selectionState.selectedBoxesOnCurrentLayerPage.length > 1;
    }

    public isAllSelectedBoxesInSelfDirectedLayout():boolean
    {
        const state = this._getState();

        for( let box of state.selectionState.selectedBoxesOnCurrentLayerPage )
        {
            while( box )
            {
                if( PositioningType.isParentDirected(box.componentDesc.layout.positioningType) )
                    return false;

                box = box.parent;
            }
        }

        return true;
    }

    @action
    public setSingleBoxSelection(box:Box):void
    {
        const state = this._getState();
        let selectedBoxesIdsByPage:Map<PageId, BoxId[]> = state.selectionState.selectedBoxesIdsByPage;

        selectedBoxesIdsByPage.set(box.page.id, [box.id]);

        this._recalcBoxLayout(box);

        EventBus.fire(this, "surfaceSelectionChanged");
    }

    @action
    public setMultipleBoxSelection(boxes:Box[]):void
    {
        const state = this._getState();
        const currentLayerPage:Page = controllers.ui.surfaceArea.getCurrentLayerPage();
        let selectedBoxesIdsByPage:Map<PageId, BoxId[]> = state.selectionState.selectedBoxesIdsByPage;

        selectedBoxesIdsByPage.set(currentLayerPage.id, boxes ? boxes.map((box:Box) => box.id) : []);

        boxes.forEach(this._recalcBoxLayout);
        
        EventBus.fire(this, "surfaceSelectionChanged");
    }

    @action
    public addBoxToSelection(box:Box):void
    {
        const state = this._getState();
        const currentLayerPage:Page = box.page;
        let selectedBoxesIdsByPage:Map<PageId, BoxId[]> = state.selectionState.selectedBoxesIdsByPage;

        if( !selectedBoxesIdsByPage.has(currentLayerPage.id) )
            selectedBoxesIdsByPage.set(currentLayerPage.id, [box.id]);
        else if( !selectedBoxesIdsByPage.get(currentLayerPage.id).find((target:BoxId) => target === box.id) )
            selectedBoxesIdsByPage.get(currentLayerPage.id).push(box.id);

        this._recalcBoxLayout(box);

        EventBus.fire(this, "surfaceSelectionChanged");
    }

    @action
    public removeBoxFromSelection(box:Box):void
    {
        const state = this._getState();
        const currentLayerPage:Page = box.page;
        let selectedBoxesIdsByPage:Map<PageId, BoxId[]> = state.selectionState.selectedBoxesIdsByPage;

        (selectedBoxesIdsByPage.get(currentLayerPage.id) as any).remove(box.id);

        EventBus.fire(this, "surfaceSelectionChanged");
    }

    @action
    public clearSelectedBoxes()
    {
        const state = this._getState();

        let selectedBoxesIdsByPage:Map<PageId, BoxId[]> = state.selectionState.selectedBoxesIdsByPage;
        Array.from(selectedBoxesIdsByPage.keys()).forEach((key:PageId) =>
            selectedBoxesIdsByPage.set(key, [])
        );

        EventBus.fire(this, "surfaceSelectionChanged");
    }

    @action
    public clearSelectedBoxesForPage()
    {
        const state = this._getState();

        const currentLayerPage:Page = controllers.ui.surfaceArea.getCurrentLayerPage();
        let selectedBoxesIdsByPage:Map<PageId, BoxId[]> = state.selectionState.selectedBoxesIdsByPage;

        selectedBoxesIdsByPage.set(currentLayerPage.id, []);

        EventBus.fire(this, "surfaceSelectionChanged");
    }

    public getSelectionBounds(adjustNegative:boolean = false):Bounds
    {
        const state = this._getState();

        const bounds:Bounds = state.selectionState.bounds;

        if( adjustNegative )
        {
            let {x, y, width, height} = bounds;

            if( width < 0 )
            {
                x += width;
                width = Math.abs(width);
            }

            if( height < 0 )
            {
                y += height;
                height = Math.abs(height);
            }

            return Bounds.from(x, y, width, height);
        }

        return bounds;
    }

    @action
    public setSelectionBounds(bounds:Bounds)
    {
        const state = this._getState();

        state.selectionState.bounds = bounds;
    }

    @action
    public clearSelectionBounds()
    {
        const state = this._getState();

        state.selectionState.bounds = null;
    }

    public getSelectionInteractionMode():SelectionInteractionMode
    {
        const state = this._getState();

        return state.selectionState.interactionMode;
    }

    @action
    public setSelectionInteractionMode(mode:SelectionInteractionMode)
    {
        const state = this._getState();

        state.selectionState.interactionMode = mode;
    }

    public isCursorSelectionEnabled():boolean
    {
        const state = this._getState();

        return state.selectionState.cursorSelectionEnabled;
    }

    @action
    public enableCursorSelection()
    {
        const state = this._getState();

        state.selectionState.cursorSelectionEnabled = true;
    }

    @action
    public disableCursorSelection()
    {
        const state = this._getState();

        this.clearSelectionBounds();

        state.selectionState.cursorSelectionEnabled = false;
    }

    public isSelectionMoving(interactionMode:SelectionInteractionMode):boolean
    {
        return [SelectionInteractionMode.StartMoving, SelectionInteractionMode.Moving].indexOf(interactionMode) !== -1;
    }

    public isSelectionResizing(interactionMode:SelectionInteractionMode):boolean
    {
        return [SelectionInteractionMode.StartResizing, SelectionInteractionMode.Resizing].indexOf(interactionMode) !== -1;
    }

    public getSelectionInteractionDirection():CompassDir
    {
        const state = this._getState();

        return state.selectionState.interactionDirection;
    }

    @action
    public setSelectionInteractionDirection(direction:CompassDir)
    {
        const state = this._getState();

        state.selectionState.interactionDirection = direction;
    }

    @action
    public addPageToSelectedBoxesIdsByPage(pageId:PageId)
    {
        let map:Map<PageId, Array<BoxId>> = this._getState().selectionState.selectedBoxesIdsByPage;
        map.set(pageId, []);
    }

    @action
    public removePageFromSelectedBoxesIdsByPage(pageId:PageId)
    {
        let map:Map<PageId, Array<BoxId>> = this._getState().selectionState.selectedBoxesIdsByPage;
        map.delete(pageId);
    }

    /**
     * todo:
     *
     * If a box is self-directed with left/right/top/bottom bound
     * and the parent of this box changed its own width/height,
     * we need to recalculate width/height of the box as well.
     * Ideally we should do it right after the parent changed the dimensions.
     *
     */
    @action.bound
    private _recalcBoxLayout(box:Box):void
    {
        if( box.componentDesc.isSelfDirected )
            LayoutUtils.recalcDimensions({
                componentDesc: box.componentDesc,
                layoutRecalc: LayoutRecalc.ALL
            });
    }

    private _findSelectedBoxIndex(state:SurfaceAreaState, box:Box):number | -1
    {
        return state.selectionState.selectedBoxesOnCurrentLayerPage.findIndex(({id}) => id === box.id);
    }

    private _getState():SurfaceAreaState
    {
        return rootState.ui.visualDesigner.surfaceEditor.area;
    }
}
